import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background: radial-gradient(1200px 600px at 50% -10%, rgba(0,153,0,0.18), transparent 60%),
              linear-gradient(180deg, var(--background-dark) 0%, var(--background-light) 100%);
  padding: 5rem 1rem 4rem;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(0,153,0,0.5);
  background: rgba(0,153,0,0.1);
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;

  span.dot { width: 8px; height: 8px; border-radius: 50%; background: var(--primary-color); box-shadow: 0 0 12px var(--primary-color); }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5.5vw, 4.5rem);
  line-height: 1.05;
  margin: 0 auto 1.25rem;
  color: var(--text-color);
  max-width: 900px;
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 auto 2rem;
  max-width: 720px;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const PrimaryLink = styled.a`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled(Link)`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const Mosaic = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  grid-template-rows: 180px 180px;
  gap: 1rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 800px) { grid-template-columns: 1fr 1fr; grid-template-rows: auto; }

  .card {
    border-radius: 1.25rem;
    overflow: hidden;
    border: 1px solid rgba(0,153,0,0.25);
    background: rgba(255,255,255,0.02);
    position: relative;
  }
  .card img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .a { grid-column: 1; grid-row: 1 / span 2; }
  .b { grid-column: 2; grid-row: 1 / span 2; }
  .c { grid-column: 3; grid-row: 1; }
  .d { grid-column: 3; grid-row: 2; }

  @media (max-width: 800px) {
    .a, .b, .c, .d { grid-column: auto; grid-row: auto; height: 180px; }
    .b { grid-column: 1 / span 2; }
  }
`;

const HeroLead: React.FC = () => (
  <Wrap>
    <Inner>
      <Eyebrow><span className="dot" /> Trusted USDT flashing software</Eyebrow>
      <Title>
        The reliable way to <span>simulate USDT balances</span> on real wallets and explorers.
      </Title>
      <Lede>
        {siteConfig.brand.name} is a licensed flash-USDT tool for developers, traders and QA teams.
        Multi-chain support across TRC-20, ERC-20 and BEP-20, 50+ compatible wallets, 24/7 support —
        without touching mainnet funds.
      </Lede>
      <Actions>
        <PrimaryLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
          Buy on WhatsApp
        </PrimaryLink>
        <GhostLink to="/pricing">View pricing plans</GhostLink>
      </Actions>

      <Mosaic>
        <div className="card a"><img src="/img/tether-hero.jpg" alt="USDT flashing across TRC-20, ERC-20 and BEP-20 networks" /></div>
        <div className="card b"><img src="/img/flash-usdt-preview.jpg" alt="USDT flasher software preview" /></div>
        <div className="card c"><img src="/img/hero-stablecoins.webp" alt="Tether stablecoin ecosystem" /></div>
        <div className="card d"><img src="/img/usdt-network.webp" alt="USDT on multiple networks" /></div>
      </Mosaic>
    </Inner>
  </Wrap>
);

export default HeroLead;
